import { RideType, PriceEstimate, Location } from '../types/rides';

type Rate = { base: number; perKm: number; perMin: number; minFare: number };

// Bolt Kenya-like rates (KES). These are the fallback defaults - the live rates
// are loaded from the backend /pricing endpoint (admin-editable) so prices can
// be updated system-wide when fuel costs change. See loadRates() below.
const RATES: Record<RideType, Rate> = {
  'motorbike': { base: 35, perKm: 18, perMin: 2, minFare: 50 },
  'safari-electric': { base: 45, perKm: 20, perMin: 3, minFare: 60 },
  'safari-basic': { base: 55, perKm: 25, perMin: 4, minFare: 80 },
  'safari-send-bike': { base: 35, perKm: 15, perMin: 1, minFare: 40 },
  'safari-send-car': { base: 60, perKm: 22, perMin: 3, minFare: 70 },
  'safari-women': { base: 60, perKm: 28, perMin: 5, minFare: 90 },
  'safari-comfort': { base: 100, perKm: 40, perMin: 8, minFare: 150 },
  'safari-xl': { base: 130, perKm: 48, perMin: 10, minFare: 200 },
};

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

// Live rate card, seeded with the defaults and refreshed from the backend. A
// short cache means an admin price change shows up in new quotes within ~20s.
let liveRates: Record<string, Rate> = { ...RATES };
// Admin-set flat promotional offer: when active, quotes show the full fare
// struck through and the rider pays `percent` % less (the discount IS the ride
// price). Kept as the fallback/legacy value for logged-out flows; a logged-in
// rider's actual discount comes from personalOfferPercent below instead.
let liveOffer: { percent: number; active: boolean } | null = null;
// Bolt-style tiered discount for the CURRENT rider (big % for a brand new
// account, decaying to a small ongoing perk as they take more rides) - see
// backend tieredOfferPercent(). Set by loadPersonalizedOffer(); null until
// that's run once, or if the admin's offer isn't active, or the call fails.
let personalOfferPercent: number | null = null;
let ratesLoadedAt = 0;
let personalOfferLoadedAt = 0;
const RATES_TTL = 20_000;

// The offer actually used for pricing: the rider's personalized tier when
// known, otherwise the flat admin offer (e.g. before loadPersonalizedOffer has
// run, or for a logged-out quote). Lets the idle map (before pickup/dropoff are
// both set) show "X% off" as soon as loadRates()/loadPersonalizedOffer() has
// run once, without needing a full ride price computation.
export function getLiveOffer(): { percent: number; active: boolean } | null {
  if (personalOfferPercent != null) return { percent: personalOfferPercent, active: true };
  return liveOffer;
}

// Fetch the logged-in rider's own tiered discount. Safe to call while logged
// out - it just leaves personalOfferPercent unset and getLiveOffer() falls
// back to the flat admin offer.
export async function loadPersonalizedOffer(): Promise<void> {
  if (Date.now() - personalOfferLoadedAt < RATES_TTL) return;
  const token = localStorage.getItem('accessToken');
  if (!token) { personalOfferPercent = null; return; }
  try {
    const res = await fetch(`${API_URL}/pricing/my-offer`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    personalOfferPercent = res.ok && data?.active && typeof data.percent === 'number' && data.percent > 0
      ? data.percent
      : null;
    personalOfferLoadedAt = Date.now();
  } catch {
    // offline / backend down - keep using the last known value
  }
}

export async function loadRates(): Promise<void> {
  if (Date.now() - ratesLoadedAt < RATES_TTL) return;
  try {
    const res = await fetch(`${API_URL}/pricing`);
    const data = await res.json();
    if (res.ok && data?.rates && typeof data.rates === 'object') {
      liveRates = { ...RATES, ...data.rates };
      const o = data.offer;
      liveOffer = o && o.active === true && typeof o.percent === 'number' && o.percent > 0 && o.percent <= 90
        ? { percent: o.percent, active: true }
        : null;
      ratesLoadedAt = Date.now();
    }
  } catch {
    // offline / backend down - keep using the last known (or default) rates
  }
}

const rateFor = (t: RideType): Rate => liveRates[t] || RATES[t];

// Stops are NOT a flat fee. Each stop is added as a waypoint in getRouteInfo()
// below, so the extra distance and time of the detour flows straight into the
// base + perKm + perMin price - a stop costs exactly what the detour is worth
// (Bolt-style), nothing more.

// Bolt's exact dynamic pricing formula[citation:2][citation:5][citation:6]
// Price = Base fare + (Distance × perKm) + (Time × perMin) × Surge multiplier

function getDynamicSurge(): number {
  const hour = new Date().getHours();
  const day = new Date().getDay();
  
  // Rush hour surge (7-9 AM, 4-7 PM on weekdays)
  if ((hour >= 7 && hour <= 9) || (hour >= 16 && hour <= 19)) {
    if (day >= 1 && day <= 5) return 1.3 + Math.random() * 0.4; // 1.3x-1.7x
  }
  
  // Weekend evening surge
  if ((day === 0 || day === 6) && hour >= 20) return 1.2 + Math.random() * 0.3;
  
  // Late night surge (11 PM - 5 AM)
  if (hour >= 23 || hour <= 5) return 1.1 + Math.random() * 0.2;
  
  // Rain/event simulation
  if (Math.random() > 0.9) return 1.5; // High demand event
  if (Math.random() > 0.8) return 1.2; // Moderate demand
  
  return 1.0; // Normal pricing
}

export async function getRouteInfo(pickup: Location, dropoff: Location, stops: Location[] = []) {
  try {
    const waypoints = [pickup, ...stops, dropoff];
    const coords = waypoints.map(p => `${p.lng},${p.lat}`).join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=false`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.routes?.[0]) {
      return {
        distance: data.routes[0].distance / 1000,
        duration: data.routes[0].duration,
        polyline: JSON.stringify(data.routes[0].geometry.coordinates),
      };
    }
  } catch (err) {}

  const allPoints = [pickup, ...stops, dropoff];
  const dist = allPoints.slice(1).reduce((sum, pt, i) => sum + calculateDistance(allPoints[i], pt), 0);
  return { distance: dist, duration: (dist / 30) * 3600, polyline: '' };
}

function calculateDistance(pickup: Location, dropoff: Location): number {
  const R = 6371;
  const dLat = (dropoff.lat - pickup.lat) * Math.PI / 180;
  const dLng = (dropoff.lng - pickup.lng) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(pickup.lat*Math.PI/180) * Math.cos(dropoff.lat*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

export function calculatePrice(rideType: RideType, distanceKm: number, durationSec: number, numStops: number = 0): PriceEstimate {
  const rates = rateFor(rideType);
  const durationMin = durationSec / 60;
  const surge = getDynamicSurge();

  // Bolt's exact formula[citation:2][citation:6]
  const basePrice = rates.base;
  const distancePrice = rates.perKm * distanceKm;
  const timePrice = rates.perMin * durationMin;
  // Stops are already baked into distanceKm/durationSec (they're routed as
  // waypoints), so there's no flat per-stop fee - the detour prices itself.
  const subtotal = basePrice + distancePrice + timePrice;
  const total = Math.max(rates.minFare, Math.round(subtotal * surge));

  // Promotional offer: rider pays the discounted amount; the full fare is kept
  // as originalPrice so the UI can strike it through. Prefers this rider's own
  // tiered percent (personalOfferPercent) over the flat admin offer - see
  // getLiveOffer().
  const effectiveOffer = getLiveOffer();
  const offered = effectiveOffer
    ? { estimatedPrice: Math.max(1, Math.round(total * (1 - effectiveOffer.percent / 100))), originalPrice: total, offerPercent: effectiveOffer.percent }
    : { estimatedPrice: total };

  return {
    rideType, ...offered, currency: 'KES',
    distance: Math.round(distanceKm * 10) / 10,
    duration: Math.round(durationMin),
    surgeActive: surge > 1.15, surgeMultiplier: surge,
  };
}

const RIDE_TYPES = [
  { id: 'motorbike' as RideType, name: 'Motorbike', icon: '🏍️', color: '#FF6B35', bgColor: 'rgba(255,107,53,0.2)', description: 'Fast & affordable', capacity: 1 },
  { id: 'safari-electric' as RideType, name: 'Safari Electric', icon: '⚡🏍️', color: '#00D084', bgColor: 'rgba(0,208,132,0.2)', description: 'Eco-friendly electric bike', capacity: 1 },
  { id: 'safari-basic' as RideType, name: 'Safari Basic', icon: '🚗', color: '#4A90D9', bgColor: 'rgba(74,144,217,0.2)', description: 'Affordable everyday ride', capacity: 4 },
  { id: 'safari-send-bike' as RideType, name: 'Safari Send', icon: '📦🏍️', color: '#FFB800', bgColor: 'rgba(255,184,0,0.2)', description: 'Parcel delivery by bike', capacity: 0 },
  { id: 'safari-send-car' as RideType, name: 'Safari Send', icon: '📦🚗', color: '#8B5CF6', bgColor: 'rgba(139,92,246,0.2)', description: 'Parcel delivery by car', capacity: 0 },
  { id: 'safari-women' as RideType, name: 'Safari Women', icon: '👩', color: '#EC4899', bgColor: 'rgba(236,72,153,0.2)', description: 'Women-driven rides', capacity: 4 },
  { id: 'safari-comfort' as RideType, name: 'Safari Comfort', icon: '🚗💎', color: '#6366F1', bgColor: 'rgba(99,102,241,0.2)', description: 'Premium comfortable ride', capacity: 4 },
  { id: 'safari-xl' as RideType, name: 'Safari XL', icon: '🚙', color: '#D97706', bgColor: 'rgba(217,119,6,0.2)', description: '7-seater for groups & families', capacity: 7 },
];

export async function getRideOptions(pickup: Location, dropoff: Location, stops: Location[] = []) {
  // Pull the latest admin-set rates (cached) before quoting, so price changes
  // propagate to the customer almost immediately.
  await loadRates();
  const route = await getRouteInfo(pickup, dropoff, stops);
  return RIDE_TYPES.map(type => {
    const r = rateFor(type.id);
    return {
      option: { ...type, basePrice: r.base, pricePerKm: r.perKm, pricePerMin: r.perMin, estimatedArrival: Math.floor(type.id === 'motorbike' ? 60 : 120 + Math.random() * 180) },
      price: calculatePrice(type.id, route.distance, route.duration, stops.length),
      route,
    };
  });
}
